/**
 * API 端点常量定义
 * 集中管理所有 API 路径
 */
export const API_ENDPOINTS = {
  // 认证相关
  AUTH: {
    LOGIN: '/auth/login',
    LOGOUT: '/auth/logout',
    ME: '/auth/me',
    REFRESH: '/auth/refresh',
    CHANGE_PASSWORD: '/auth/change-password',
  },

  // 员工管理
  EMPLOYEES: {
    LIST: '/employees',
    DIRECTORY_SEARCH: '/employees/directory-search',
    DETAIL: (id: string | number) => `/employees/${id}`,
    CREATE: '/employees',
    UPDATE: (id: string | number) => `/employees/${id}`,
    DELETE: (id: string | number) => `/employees/${id}`,
    SYNC_WECHAT: '/employees/sync-wechat',
    REFRESH_AVATARS: '/employees/avatars/refresh',
    WRITEBACK_NAMES: '/employees/writeback-names',
    BATCH_IMPORT: '/employees/batch-import',
    EXPORT: '/employees/export',
  },

  // 会话存档
  CONVERSATIONS: {
    LIST: '/conversations',
    DETAIL: (id: string | number) => `/conversations/${id}`,
    MESSAGES: (id: string | number) => `/conversations/${id}/messages`,
    CONVERSATION_INHERIT_SIDEBAR_BY_EXTERNAL_CONTACT: '/conversations/conversation-inherit-sidebar/by-external-contact',
    CONVERSATION_INHERIT_SIDEBAR_BY_ROOM: '/conversations/conversation-inherit-sidebar/by-room',
    CONVERSATION_INHERIT_SIDEBAR_RELATED_CONVERSATIONS: '/conversations/conversation-inherit-sidebar/related-conversations',
    CONVERSATION_INHERIT_SIDEBAR_MESSAGES: (id: string | number) => `/conversations/conversation-inherit-sidebar/${id}/messages`,
    EXPORT: (id: string | number) => `/conversations/${id}/export`,
    PIN: (id: string | number) => `/conversations/${id}/pin`,
    STATISTICS: '/conversations/statistics',
    ARCHIVE_SYNC_STATUS: '/conversations/archive-sync/status',
    ARCHIVE_SYNC_RUN: '/conversations/archive-sync/run',
    MEDIA: (messageId: string | number) => `/conversations/media/${messageId}`,
    MEDIA_FILES: '/conversations/media-files',
    MEDIA_FILE_CONTEXT: (messageId: string | number) => `/conversations/media-files/${messageId}/context`,
    MESSAGE_CONTEXT: '/conversations/messages/context',
    PREPARE_MESSAGE_CONTEXT: '/conversations/messages/context/prepare',
  },

  // 会话导出
  CONVERSATION_EXPORT: {
    TASKS: '/conversation-export/tasks',
    REFRESH: (id: string | number) => `/conversation-export/tasks/${id}/refresh`,
    SUBMIT: (id: string | number) => `/conversation-export/tasks/${id}/submit`,
    DOWNLOADED: (id: string | number) => `/conversation-export/tasks/${id}/downloaded`,
    DETAIL_SUBMIT: (id: string | number) => `/conversation-export/details/${id}/submit`,
    DETAIL_REFRESH: (id: string | number) => `/conversation-export/details/${id}/refresh`,
    DETAIL_DOWNLOADED: (id: string | number) => `/conversation-export/details/${id}/downloaded`,
    BATCH_SUBMIT: (id: string | number) => `/conversation-export/tasks/${id}/batch-submit`,
    BATCH_REFRESH: (id: string | number) => `/conversation-export/batches/${id}/refresh`,
    BATCH_DOWNLOADED: (id: string | number) => `/conversation-export/batches/${id}/downloaded`,
  },

  // 风险预警
  ALERTS: {
    LIST: '/alerts',
    WORKBENCH_RECENT: '/alerts/workbench/recent',
    DETAIL: (id: string | number) => `/alerts/${id}`,
    HANDLE: (id: string | number) => `/alerts/${id}/handle`,
    STATISTICS: '/alerts/statistics',
    TIMEOUT_EMPLOYEE_STATISTICS: '/alerts/statistics/timeout-employees',
    BATCH_HANDLE: '/alerts/batch-handle',
    CLEAR_PENDING: '/alerts/clear-pending',
  },

  RISK_DATA: '/risk-data',

  // 客户流失
  CUSTOMER_LOSS: {
    LIST: '/customer-loss',
    STATISTICS: '/customer-loss/statistics',
    HANDLE: (id: string | number) => `/customer-loss/${id}/handle`,
    BULK_HANDLE: '/customer-loss/bulk-handle',
  },

  // 拒绝列表
  ARCHIVE_REJECTIONS: {
    LIST: '/archive-rejections',
    STATISTICS: '/archive-rejections/statistics',
    HANDLE: (id: string | number) => `/archive-rejections/${id}/handle`,
    BULK_HANDLE: '/archive-rejections/bulk-handle',
  },

  // 负面消息
  NEGATIVE_MESSAGES: {
    LIST: '/negative-messages',
    STATISTICS: '/negative-messages/statistics',
    AUDIT: (id: string | number) => `/negative-messages/${id}/audit`,
  },

  // 规则配置
  RULES: {
    LIST: '/rules',
    DETAIL: (id: string | number) => `/rules/${id}`,
    CREATE: '/rules',
    UPDATE: (id: string | number) => `/rules/${id}`,
    DELETE: (id: string | number) => `/rules/${id}`,
    TOGGLE: (id: string | number) => `/rules/${id}/toggle`,
  },

  // 敏感词规则（引用词表基础资料）
  SENSITIVE_WORD_RULES: {
    LIST: '/sensitive-word-rules',
    HITS: '/sensitive-word-rules/hits',
    HIT_STATISTICS: '/sensitive-word-rules/hits/statistics',
    HIT_AUDIT: (id: string | number) => `/sensitive-word-rules/hits/${id}/audit`,
    HITS_BULK_AUDIT: '/sensitive-word-rules/hits/bulk-audit',
    CREATE: '/sensitive-word-rules',
    UPDATE: (id: string | number) => `/sensitive-word-rules/${id}`,
    DELETE: (id: string | number) => `/sensitive-word-rules/${id}`,
    TOGGLE: (id: string | number) => `/sensitive-word-rules/${id}/toggle`,
    SYNC: (id: string | number) => `/sensitive-word-rules/${id}/sync`,
  },

  // 词表基础资料（与风险规则独立）
  WORD_LISTS: {
    LIST: '/word-lists',
    DETAIL: (id: string | number) => `/word-lists/${id}`,
    CREATE: '/word-lists',
    UPDATE: (id: string | number) => `/word-lists/${id}`,
    DELETE: (id: string | number) => `/word-lists/${id}`,
  },

  // 应用管理
  AGENTS: {
    LIST: '/agents',
    DETAIL: (id: string | number) => `/agents/${id}`,
    CREATE: '/agents',
    UPDATE: (id: string | number) => `/agents/${id}`,
    DELETE: (id: string | number) => `/agents/${id}`,
  },

  // AI 相关
  AI: {
    CHAT: '/ai/chat',
    SUMMARIZE: '/ai/summarize',
    ANALYZE_RISK: '/ai/analyze-risk',
  },

  // 统计报表
  REPORTS: {
    OVERVIEW: '/reports/overview',
    CONVERSATION_TREND: '/reports/conversation-trend',
    RISK_DISTRIBUTION: '/reports/risk-distribution',
    EMPLOYEE_RANKING: '/reports/employee-ranking',
    WORKBENCH_EMPLOYEE_RANKING: '/reports/workbench/employee-ranking',
    DEPARTMENT_RANKING: '/reports/department-ranking',
    CONTACT_CUSTOMER_DETAIL: '/reports/contact-customer-detail',
    GROUP_CHAT_DETAIL: '/reports/group-chat-detail',
    EXPORT: '/reports/export',
  },

  // 系统设置
  SETTINGS: {
    ROLES: '/settings/roles',
    ADMINS: '/settings/admins',
    LICENSE_QUOTA: '/settings/license-quota',
    ADMIN_DETAIL: (id: string | number) => `/settings/admins/${id}`,
    ROLE_DETAIL: (id: string | number) => `/settings/roles/${id}`,
    ROLE_MEMBERS: (roleId: string | number) => `/settings/roles/${roleId}/members`,
    CREATOR_TRANSFER: '/settings/roles/creator/transfer',
    OPERATION_LOGS: '/settings/logs',
    WHITELIST: '/settings/whitelist',
    SYSTEM_CONFIG: '/settings/system-config',
    WECOM_CONFIG: '/settings/wecom-config',
    WECOM_TEST: '/settings/wecom-config/test',
    WECOM_DATAZONE_STATUS: '/settings/wecom-config/datazone/status',
    WECOM_DATAZONE_AUTH_USERS: '/settings/wecom-config/datazone/auth-users',
    WECOM_DATAZONE_DOC_AUTH_INFO: '/settings/wecom-config/datazone/doc-auth-info',
    WECOM_DATAZONE_PUBLIC_KEY: '/settings/wecom-config/datazone/public-key',
    WECOM_DATAZONE_RECEIVE_CALLBACK: '/settings/wecom-config/datazone/receive-callback',
    // 超时提醒规则
    TIMEOUT_RULES: '/timeout-rules',
    TIMEOUT_RULE_DETAIL: (id: string | number) => `/timeout-rules/${id}`,
    TIMEOUT_RULE_TOGGLE: (id: string | number) => `/timeout-rules/${id}/toggle`,
  },

  // 会话继承
  INHERIT: {
    OPERATIONS_EXECUTE: '/inherit/operations/execute',
    OPERATIONS: '/inherit/operations',
    OPERATION_DETAIL: (id: number) => `/inherit/operations/${id}`,
    OPERATION_RESULTS: (id: number) => `/inherit/operations/${id}/results`,
    OPERATION_DELETE: (id: number) => `/inherit/operations/${id}`,
    // 离职继承
    UNASSIGNED_LIST: '/inherit/offboarding/unassigned',
    UNASSIGNED_GROUPS: '/inherit/offboarding/unassigned/groups',
    OFFBOARDING_ASSIGN: '/inherit/offboarding/assign',
    OFFBOARDING_INHERIT: '/inherit/offboarding/inherit',
    OFFBOARDING_OPERATIONS: '/inherit/offboarding/operations',
    OFFBOARDING_OPERATION_DETAIL: (id: number) => `/inherit/offboarding/operations/${id}`,
    OFFBOARDING_OPERATION_DELETE: (id: number) => `/inherit/offboarding/operations/${id}`,
    OFFBOARDING_SYNC: '/inherit/offboarding/sync',
    OFFBOARDING_RECORDS: '/inherit/offboarding/records',
    OFFBOARDING_RECORD_DETAIL: (id: number) => `/inherit/offboarding/records/${id}`,
    // 在职继承
    TRANSFERABLE_LIST: '/inherit/onjob/transferable',
    ONJOB_TRANSFER: '/inherit/onjob/transfer',
    ONJOB_RECORDS: '/inherit/onjob/records',
    ONJOB_RECORD_DETAIL: (id: number) => `/inherit/onjob/records/${id}`,
    // 侧边栏
    CONVERSATION_INHERIT_SIDEBAR_CONFIG: '/inherit/conversation-inherit-sidebar/config',
    CONVERSATION_INHERIT_SIDEBAR_SYNC: '/inherit/conversation-inherit-sidebar/sync',
    // 统计
    STATISTICS: '/inherit/statistics',
  },

  // 部门管理
  DEPARTMENTS: {
    LIST: '/departments',
    TREE: '/departments/tree',
    DETAIL: (id: string | number) => `/departments/${id}`,
    CREATE: '/departments',
    UPDATE: (id: string | number) => `/departments/${id}`,
    DELETE: (id: string | number) => `/departments/${id}`,
    SYNC_WECOM: '/departments/sync-wecom',
  },

  // 用户管理
  USERS: {
    LIST: '/users',
    DETAIL: (id: string | number) => `/users/${id}`,
    CREATE: '/users',
    UPDATE: (id: string | number) => `/users/${id}`,
    DELETE: (id: string | number) => `/users/${id}`,
    RESET_PASSWORD: (id: string | number) => `/users/${id}/reset-password`,
  },
} as const;
